import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import useMobileView from '../../hooks/useMobileView';
import { mobileHideScrollbarSx } from '../layout/mobile/mobileAppTheme';

const DATE_PRESET_OPTIONS = [
  { key: 'today', label: 'Today', icon: <CalendarTodayIcon fontSize="small" /> },
  { key: 'yesterday', label: 'Yesterday', icon: <ReplayIcon fontSize="small" /> },
  { key: 'last_7_days', label: 'Last 7 Days', icon: <CalendarTodayIcon fontSize="small" /> },
  { key: 'last_30_days', label: 'Last 30 Days', icon: <CalendarTodayIcon fontSize="small" /> },
  { key: 'this_month', label: 'This Month', icon: <CalendarTodayIcon fontSize="small" /> },
  { key: 'last_month', label: 'Last Month', icon: <ReplayIcon fontSize="small" /> },
  { key: 'custom', label: 'Custom Date', icon: <CalendarTodayIcon fontSize="small" /> },
];

export default function DashboardReportHeader({
  title,
  subtitle,
  startDate,
  endDate,
  onDateRangeChange,
  onRefresh,
  onPdfDownload,
  loading = false,
  pdfExporting = false,
  pdfDisabled = false,
  themeMode = 'light',
  accentColor = '#4ce1b6',
  initialPreset = 'this_month',
  showPdf = true,
  showDateFilter = true,
  showExcel = false,
  showCsv = false,
  showPrint = false,
  onExcelDownload,
  onCsvDownload,
  onPrintDownload,
  allowAllPreset = false,
  actionsOnly = false,
}) {
  const { isCompact } = useMobileView();
  const isDark = themeMode === 'dark';
  const datePresetOptions = useMemo(
    () => (allowAllPreset
      ? [{ key: 'all', label: 'All', icon: <CalendarTodayIcon fontSize="small" /> }, ...DATE_PRESET_OPTIONS]
      : DATE_PRESET_OPTIONS),
    [allowAllPreset]
  );
  const [datePreset, setDatePreset] = useState(initialPreset);
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [showCustomDates, setShowCustomDates] = useState(initialPreset === 'custom');
  const selectedPreset = useMemo(
    () => datePresetOptions.find((option) => option.key === datePreset)
      || datePresetOptions.find((option) => option.key === (allowAllPreset ? 'all' : 'this_month'))
      || datePresetOptions[0],
    [allowAllPreset, datePreset, datePresetOptions]
  );

  const applyDatePreset = (presetKey) => {
    const today = moment().startOf('day');
    setDatePreset(presetKey);
    setDateMenuAnchor(null);

    if (presetKey === 'custom') {
      setShowCustomDates(true);
      return;
    }

    setShowCustomDates(false);
    if (allowAllPreset && presetKey === 'all') {
      onDateRangeChange(null, null, presetKey);
    } else if (presetKey === 'today') {
      onDateRangeChange(today.clone(), today.clone(), presetKey);
    } else if (presetKey === 'yesterday') {
      const yesterday = today.clone().subtract(1, 'day');
      onDateRangeChange(yesterday.clone(), yesterday.clone(), presetKey);
    } else if (presetKey === 'last_7_days') {
      onDateRangeChange(today.clone().subtract(6, 'days'), today.clone(), presetKey);
    } else if (presetKey === 'last_30_days') {
      onDateRangeChange(today.clone().subtract(29, 'days'), today.clone(), presetKey);
    } else if (presetKey === 'last_month') {
      const lastMonth = today.clone().subtract(1, 'month');
      onDateRangeChange(lastMonth.clone().startOf('month'), lastMonth.clone().endOf('month'), presetKey);
    } else if (presetKey === 'this_month') {
      onDateRangeChange(today.clone().startOf('month'), today.clone().endOf('month'), presetKey);
    } else {
      onDateRangeChange(today.clone().startOf('month'), today.clone().endOf('month'), 'this_month');
    }
  };

  const applyCustomStartDate = (dateValue) => {
    if (!dateValue || !dateValue.isValid()) return;
    const nextEnd = !endDate || dateValue.isAfter(endDate) ? dateValue.clone() : endDate;
    onDateRangeChange(dateValue.clone(), nextEnd.clone(), 'custom');
    setDatePreset('custom');
  };

  const applyCustomEndDate = (dateValue) => {
    if (!dateValue || !dateValue.isValid()) return;
    const nextStart = startDate || dateValue.clone();
    onDateRangeChange(nextStart.clone(), dateValue.clone(), 'custom');
    setDatePreset('custom');
  };

  const compactControlHeight = 36;

  const exportButtonSx = {
    minWidth: isCompact ? 'auto' : 132,
    flexShrink: 0,
    minHeight: isCompact ? compactControlHeight : undefined,
    height: isCompact ? compactControlHeight : undefined,
    px: isCompact ? 1 : 1.6,
    py: isCompact ? 0 : 0.95,
    borderRadius: isCompact ? '8px' : '10px',
    textTransform: 'none',
    fontWeight: 800,
    fontSize: isCompact ? '0.8125rem' : undefined,
    letterSpacing: 0.2,
    whiteSpace: 'nowrap',
    color: isDark ? '#f4f7fb' : '#172033',
    borderColor: isDark ? alpha('#ffffff', 0.14) : alpha('#101828', 0.16),
    bgcolor: isDark ? alpha('#ffffff', 0.045) : '#ffffff',
    boxShadow: isDark ? '0 10px 24px rgba(8, 12, 24, 0.22)' : '0 10px 24px rgba(16, 24, 40, 0.08)',
    '& .MuiButton-startIcon': { mr: isCompact ? 0.45 : 0.9 },
    '&:hover': {
      borderColor: alpha(accentColor, 0.7),
      bgcolor: isDark ? alpha(accentColor, 0.12) : alpha(accentColor, 0.08),
    },
    '&.Mui-disabled': {
      color: isDark ? alpha('#ffffff', 0.36) : alpha('#101828', 0.36),
      borderColor: isDark ? alpha('#ffffff', 0.08) : alpha('#101828', 0.08),
    },
  };

  const datePresetButtonSx = {
    minWidth: isCompact ? 'auto' : 175,
    width: 'auto',
    flex: '0 0 auto',
    flexShrink: 0,
    minHeight: isCompact ? compactControlHeight : undefined,
    height: isCompact ? compactControlHeight : undefined,
    justifyContent: 'center',
    px: isCompact ? 1.15 : 1.9,
    py: isCompact ? 0 : 0.95,
    borderRadius: isCompact ? '8px' : '10px',
    textTransform: 'none',
    fontWeight: 800,
    fontSize: isCompact ? '0.8125rem' : undefined,
    letterSpacing: 0.2,
    whiteSpace: 'nowrap',
    color: '#02221b',
    background: 'linear-gradient(180deg, #58f2ce 0%, #44e8bf 100%)',
    border: '1px solid rgba(88, 242, 206, 0.85)',
    boxShadow: isCompact
      ? '0 6px 14px rgba(36, 226, 178, 0.22)'
      : '0 10px 26px rgba(36, 226, 178, 0.28)',
    '& .MuiButton-startIcon': {
      mr: isCompact ? 0.5 : 1,
      color: '#02221b',
      '& > *:nth-of-type(1)': { fontSize: isCompact ? 16 : undefined },
    },
    '& .MuiButton-endIcon': {
      ml: isCompact ? 0.35 : 1,
      '& > *:nth-of-type(1)': { fontSize: isCompact ? 18 : undefined },
    },
    '&:hover': {
      background: 'linear-gradient(180deg, #6cf8d7 0%, #52ecc7 100%)',
    },
  };

  // Mobile app shell (phone + tablet): stack title above actions so md+ never
  // places the control row over the heading text.
  const stackDirection = isCompact ? 'column' : { xs: 'column', md: 'row' };
  const stackAlignItems = isCompact ? 'stretch' : { xs: 'flex-start', md: 'center' };

  const actionRowSx = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: isCompact ? 'flex-end' : 'flex-start',
    gap: isCompact ? 0.75 : 1,
    width: isCompact ? '100%' : 'auto',
    maxWidth: '100%',
    minWidth: 0,
    flexShrink: 0,
    alignSelf: isCompact ? 'stretch' : 'auto',
    overflowX: isCompact ? 'auto' : 'visible',
    WebkitOverflowScrolling: 'touch',
    ...(isCompact ? { ...mobileHideScrollbarSx, pb: 0.25 } : {}),
  };

  const actionRow = (
    <Box sx={actionRowSx}>
      {showDateFilter && showCustomDates && (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'nowrap',
              gap: 1.2,
              p: 1,
              borderRadius: 2,
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              bgcolor: isDark ? '#20232d' : '#ffffff',
              flexShrink: 0,
            }}
          >
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={applyCustomStartDate}
              maxDate={endDate || undefined}
              slotProps={{ textField: { size: 'small', sx: { width: 150, flexShrink: 0 } } }}
            />
            <Typography color="text.secondary" sx={{ flexShrink: 0 }}>
              to
            </Typography>
            <DatePicker
              label="End date"
              value={endDate}
              onChange={applyCustomEndDate}
              minDate={startDate || undefined}
              maxDate={startDate ? startDate.clone().add(3, 'months') : undefined}
              slotProps={{ textField: { size: 'small', sx: { width: 150, flexShrink: 0 } } }}
            />
          </Box>
        </LocalizationProvider>
      )}

      {showDateFilter && (
        <>
          <Button
            variant="contained"
            startIcon={selectedPreset.icon}
            endIcon={<KeyboardArrowDownIcon sx={{ color: '#02221b' }} />}
            onClick={(event) => setDateMenuAnchor(event.currentTarget)}
            disabled={loading}
            sx={datePresetButtonSx}
          >
            {selectedPreset.label}
          </Button>

          <Menu
            anchorEl={dateMenuAnchor}
            open={Boolean(dateMenuAnchor)}
            onClose={() => setDateMenuAnchor(null)}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 230,
                borderRadius: '12px',
                border: `1px solid ${alpha(accentColor, 0.22)}`,
                background: isDark
                  ? 'linear-gradient(180deg, #242937 0%, #1d2130 100%)'
                  : 'linear-gradient(180deg, #293041 0%, #1f2432 100%)',
                boxShadow: '0 18px 34px rgba(8, 12, 24, 0.45)',
                p: 0.55,
              },
            }}
            MenuListProps={{ sx: { py: 0.2 } }}
          >
            {datePresetOptions.map((option) => (
              <MenuItem
                key={option.key}
                selected={datePreset === option.key}
                onClick={() => applyDatePreset(option.key)}
                sx={{
                  my: 0.35,
                  mx: 0.35,
                  px: 1.1,
                  py: 0.95,
                  gap: 1.2,
                  borderRadius: '10px',
                  color: '#f2f7ff',
                  fontWeight: datePreset === option.key ? 800 : 700,
                  '&.Mui-selected': { bgcolor: alpha(accentColor, 0.24) },
                  '&.Mui-selected:hover': { bgcolor: alpha(accentColor, 0.28) },
                  '&:hover': { bgcolor: alpha(accentColor, 0.16) },
                }}
              >
                {option.icon}
                {option.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {showPdf && (
        <Button
          variant="outlined"
          startIcon={<PictureAsPdfIcon />}
          onClick={onPdfDownload}
          disabled={pdfDisabled || loading || pdfExporting}
          sx={exportButtonSx}
        >
          {pdfExporting ? 'Exporting...' : isCompact ? 'PDF' : 'PDF Report'}
        </Button>
      )}

      {showExcel && (
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={onExcelDownload}
          disabled={pdfDisabled || loading || pdfExporting}
          sx={exportButtonSx}
        >
          Excel
        </Button>
      )}

      {showCsv && (
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={onCsvDownload}
          disabled={pdfDisabled || loading || pdfExporting}
          sx={exportButtonSx}
        >
          CSV
        </Button>
      )}

      {showPrint && (
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={onPrintDownload}
          disabled={pdfDisabled || loading || pdfExporting}
          sx={exportButtonSx}
        >
          Print
        </Button>
      )}

      {onRefresh && (
        <IconButton
          onClick={onRefresh}
          disabled={loading}
          size={isCompact ? 'small' : 'medium'}
          sx={{
            width: isCompact ? compactControlHeight : 42,
            height: isCompact ? compactControlHeight : 42,
            flexShrink: 0,
            border: `1px solid ${isDark ? alpha('#ffffff', 0.1) : alpha('#101828', 0.12)}`,
            bgcolor: isDark ? alpha('#ffffff', 0.04) : '#ffffff',
            color: isDark ? '#e7efff' : '#172033',
            '&:hover': { bgcolor: isDark ? alpha('#ffffff', 0.08) : alpha('#101828', 0.04) },
            '& .MuiSvgIcon-root': { fontSize: isCompact ? 18 : 24 },
          }}
        >
          <RefreshIcon />
        </IconButton>
      )}
    </Box>
  );

  return (
    <>
      {pdfExporting && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            background: isDark
              ? 'radial-gradient(circle at center, rgba(76, 225, 182, 0.16) 0%, rgba(7, 12, 20, 0.72) 42%, rgba(7, 12, 20, 0.9) 100%)'
              : 'radial-gradient(circle at center, rgba(76, 225, 182, 0.2) 0%, rgba(248, 250, 252, 0.72) 44%, rgba(226, 232, 240, 0.86) 100%)',
            backdropFilter: 'blur(10px) saturate(135%)',
            WebkitBackdropFilter: 'blur(10px) saturate(135%)',
          }}
        >
          <Box
            sx={{
              width: 'min(430px, 92vw)',
              p: { xs: 3, sm: 4 },
              textAlign: 'center',
              borderRadius: '28px',
              position: 'relative',
              overflow: 'hidden',
              border: `1px solid ${alpha(accentColor, isDark ? 0.42 : 0.34)}`,
              background: isDark
                ? 'linear-gradient(145deg, rgba(30, 35, 48, 0.92) 0%, rgba(18, 23, 35, 0.96) 100%)'
                : 'linear-gradient(145deg, rgba(255, 255, 255, 0.92) 0%, rgba(241, 245, 249, 0.96) 100%)',
              boxShadow: isDark
                ? `0 28px 70px rgba(0, 0, 0, 0.46), 0 0 42px ${alpha(accentColor, 0.22)}`
                : `0 28px 70px rgba(15, 23, 42, 0.22), 0 0 42px ${alpha(accentColor, 0.28)}`,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -80,
                background: `conic-gradient(from 180deg, transparent, ${alpha(accentColor, 0.32)}, transparent 32%)`,
                animation: 'roomnexaReportSpin 2.4s linear infinite',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 1.5,
                borderRadius: '26px',
                background: isDark
                  ? 'linear-gradient(145deg, rgba(30, 35, 48, 0.96) 0%, rgba(18, 23, 35, 0.98) 100%)'
                  : 'linear-gradient(145deg, rgba(255, 255, 255, 0.96) 0%, rgba(241, 245, 249, 0.98) 100%)',
              },
              '@keyframes roomnexaReportSpin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box
                sx={{
                  width: 78,
                  height: 78,
                  mx: 'auto',
                  mb: 2.2,
                  borderRadius: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#06251f',
                  background: `linear-gradient(180deg, ${alpha(accentColor, 0.98)} 0%, ${alpha(accentColor, 0.78)} 100%)`,
                  boxShadow: `0 18px 34px ${alpha(accentColor, 0.28)}`,
                }}
              >
                <PictureAsPdfIcon sx={{ fontSize: 36 }} />
              </Box>
              <Typography
                sx={{
                  mt: 0.5,
                  color: isDark ? alpha('#e2e8f0', 0.78) : alpha('#334155', 0.78),
                  fontSize: 14,
                  lineHeight: 1.55,
                  fontWeight: 600,
                }}
              >
                Please wait while RoomNexa prepares charts, tables, and a polished report layout for export.
              </Typography>
              <Box
                sx={{
                  mt: 2.8,
                  mx: 'auto',
                  width: '72%',
                  height: 6,
                  borderRadius: 999,
                  overflow: 'hidden',
                  backgroundColor: isDark ? alpha('#ffffff', 0.08) : alpha('#0f172a', 0.08),
                }}
              >
                <Box
                  sx={{
                    width: '42%',
                    height: '100%',
                    borderRadius: 999,
                    background: `linear-gradient(90deg, ${alpha(accentColor, 0.2)} 0%, ${accentColor} 100%)`,
                    animation: 'roomnexaReportProgress 1.2s ease-in-out infinite alternate',
                    '@keyframes roomnexaReportProgress': {
                      '0%': { transform: 'translateX(-35%)' },
                      '100%': { transform: 'translateX(175%)' },
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {actionsOnly ? (
        actionRow
      ) : (
        <Stack
          direction={stackDirection}
          justifyContent="space-between"
          alignItems={stackAlignItems}
          spacing={isCompact ? 1.5 : 1.2}
          sx={{
            px: { xs: 0, sm: 2 },
            py: { xs: 0, sm: 1.2 },
            mb: { xs: 2, sm: 3 },
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            borderBottom: '1px solid',
            borderColor: isDark ? alpha('#ffffff', 0.08) : alpha('#101828', 0.08),
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              flex: isCompact ? '0 0 auto' : { md: '1 1 auto' },
              width: isCompact ? '100%' : { xs: '100%', md: 'auto' },
              pr: isCompact ? 0 : { md: 2 },
            }}
          >
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                letterSpacing: -0.3,
                fontSize: { xs: '1.15rem', sm: '1.5rem' },
                color: isDark ? '#ffffff' : undefined,
                wordBreak: 'break-word',
              }}
              component="h2"
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.4,
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                  color: isDark ? 'rgba(226, 232, 240, 0.72)' : undefined,
                  maxWidth: isCompact ? '100%' : { md: 520 },
                  pr: 0,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {actionRow}
        </Stack>
      )}
    </>
  );
}
